import { AnimatePresence, motion } from "motion/react";
import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { createPortal } from "react-dom";
import { Link, useRouterState } from "@tanstack/react-router";
import { Icon, MODULE_ICON, type IconName } from "./Icon";
import { initials } from "../lib/format";

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(" ");
export { cx };

// ── pulsanti ──────────────────────────────────────────────────────────
type Variant = "primary" | "secondary" | "ghost" | "danger" | "onModule" | "onModuleGhost" | "module";
const VARIANTS: Record<Variant, string> = {
  primary: "bg-ink text-on-ink hover:bg-ink-hover border-0",
  module: "bg-[var(--module)] text-white hover:brightness-95 border-0",
  secondary: "bg-surface text-ink border border-line hover:bg-ground",
  ghost: "bg-transparent text-text2 hover:bg-hover border-0",
  danger: "bg-surface text-danger border border-danger-line hover:bg-danger-tint",
  onModule: "bg-surface text-[var(--module)] font-semibold border-0 hover:bg-surface/90",
  onModuleGhost: "bg-transparent text-white border border-white/45 hover:bg-surface/10",
};

export function Button({ variant = "secondary", size = "md", icon, iconRight, loading, children, className, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: "sm" | "md" | "lg"; icon?: IconName; iconRight?: IconName; loading?: boolean }) {
  const h = size === "sm" ? "h-[34px] px-3 text-[13px] rounded-lg" : size === "lg" ? "h-[54px] px-5 text-base rounded-[14px] font-semibold" : "h-9 px-3.5 text-sm rounded-[9px]";
  return (
    <button type="button" {...rest} disabled={rest.disabled || loading} className={cx("inline-flex items-center justify-center gap-2 font-medium cursor-pointer whitespace-nowrap transition-colors disabled:opacity-55 disabled:cursor-not-allowed", h, VARIANTS[variant], className)}>
      {loading ? <Spinner size={15} /> : icon ? <Icon name={icon} size={15} strokeWidth={variant === "onModule" || variant === "primary" ? 2 : 1.8} /> : null}
      {children}
      {iconRight && <Icon name={iconRight} size={14} />}
    </button>
  );
}

export function IconButton({ icon, label, className, size = 32, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { icon: IconName; label: string; size?: number }) {
  return (
    <button type="button" aria-label={label} title={label} {...rest} className={cx("inline-flex items-center justify-center rounded-lg text-text2 hover:bg-hover cursor-pointer", className)} style={{ width: size, height: size }}>
      <Icon name={icon} size={16} />
    </button>
  );
}

export function Spinner({ size = 18 }: { size?: number }) {
  return (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" className="animate-spin">
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

// ── campi ─────────────────────────────────────────────────────────────
export function Field({ label, hint, error, children, htmlFor }: { label: string; hint?: string; error?: string; children: ReactNode; htmlFor?: string }) {
  return (
    <div className="flex flex-col gap-1.5 min-w-0">
      <label htmlFor={htmlFor} className="text-[13px] font-medium text-text2">
        {label}
      </label>
      {children}
      {error ? <span className="text-xs text-danger">{error}</span> : hint ? <span className="text-xs text-muted">{hint}</span> : null}
    </div>
  );
}

const inputCls = "h-10 w-full rounded-[10px] border border-line bg-surface px-3 text-sm text-ink placeholder:text-subtle focus:border-ink focus:outline-none transition-colors";

export function Input({ label, hint, error, className, ...rest }: InputHTMLAttributes<HTMLInputElement> & { label?: string; hint?: string; error?: string }) {
  const id = useId();
  const input = <input id={rest.id ?? id} {...rest} className={cx(inputCls, error && "border-danger", className)} />;
  return label ? (
    <Field label={label} hint={hint} error={error} htmlFor={rest.id ?? id}>
      {input}
    </Field>
  ) : (
    input
  );
}

export function Textarea({ label, hint, className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement> & { label?: string; hint?: string }) {
  const id = useId();
  const el = <textarea id={id} rows={4} {...rest} className={cx(inputCls, "h-auto py-2.5 leading-relaxed resize-y", className)} />;
  return label ? (
    <Field label={label} hint={hint} htmlFor={id}>
      {el}
    </Field>
  ) : (
    el
  );
}

export function Select({ label, hint, options, className, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & { label?: string; hint?: string; options: { value: string; label: string }[] }) {
  const id = useId();
  const el = (
    <select id={id} {...rest} className={cx(inputCls, "pr-8 appearance-none bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2214%22 height=%2214%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%238A8D93%22 stroke-width=%222%22><path d=%22M6 9l6 6 6-6%22/></svg>')] bg-no-repeat bg-[right_10px_center]", className)}>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
  return label ? (
    <Field label={label} hint={hint} htmlFor={id}>
      {el}
    </Field>
  ) : (
    el
  );
}

export function Checkbox({ label, checked, onChange, description }: { label: string; checked: boolean; onChange: (v: boolean) => void; description?: string }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer text-sm">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[var(--module)]" />
      <span className="flex flex-col">
        <span className="font-medium">{label}</span>
        {description && <span className="text-xs text-muted">{description}</span>}
      </span>
    </label>
  );
}

/** Interruttore accessibile (role switch). */
export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button type="button" role="switch" aria-checked={checked} aria-label={label} disabled={disabled} onClick={() => onChange(!checked)} className={cx("relative h-6 w-10 rounded-full transition-colors cursor-pointer disabled:opacity-50", checked ? "bg-ink" : "bg-line-strong")}>
      <span className={cx("absolute top-0.5 h-5 w-5 rounded-full bg-surface shadow transition-[left]", checked ? "left-[18px]" : "left-0.5")} />
    </button>
  );
}

// ── avatar ────────────────────────────────────────────────────────────
const AVATAR_COLORS = ["#F0B429", "#5B6CF0", "#C96BA8", "#3F9C8C", "#E07A5F", "#6B8E23", "#8E7DBE", "#2C6E91"];
export function avatarColor(seed: string) {
  let h = 0;
  for (const c of seed) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length]!;
}

export function Avatar({ user, size = 28, ring }: { user: { id?: string; firstName: string; lastName?: string | null } | null | undefined; size?: number; ring?: string }) {
  if (!user)
    return <span aria-hidden="true" style={{ width: size, height: size, borderRadius: "50%", border: "1px dashed var(--color-line-strong)", display: "inline-block", flexShrink: 0 }} />;
  const bg = avatarColor(user.id ?? user.firstName);
  const dark = bg === "#F0B429";
  return (
    <span
      title={`${user.firstName} ${user.lastName ?? ""}`.trim()}
      style={{ width: size, height: size, borderRadius: "50%", background: bg, color: dark ? "#101214" : "#fff", fontSize: Math.max(9, Math.round(size * 0.38)), fontWeight: 600, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, border: ring ? `2px solid ${ring}` : undefined }}
    >
      {initials(user.firstName, user.lastName ?? "")}
    </span>
  );
}

export function AvatarStack({ users, ring = "var(--color-surface)", max = 4, size = 28 }: { users: { id?: string; firstName: string; lastName?: string | null }[]; ring?: string; max?: number; size?: number }) {
  return (
    <span className="flex">
      {users.slice(0, max).map((u, i) => (
        <span key={u.id ?? i} style={{ marginLeft: i ? -8 : 0 }}>
          <Avatar user={u} size={size} ring={ring} />
        </span>
      ))}
      {users.length > max && <span style={{ marginLeft: -8, width: size, height: size, border: `2px solid ${ring}` }} className="rounded-full bg-line-soft text-[11px] font-semibold flex items-center justify-center text-muted">+{users.length - max}</span>}
    </span>
  );
}

// ── badge ─────────────────────────────────────────────────────────────
type Tone = "neutral" | "module" | "green" | "orange" | "violet" | "blue" | "warn" | "red";
const TONES: Record<Tone, string> = {
  neutral: "bg-line-soft text-text2",
  module: "bg-[var(--module-tint)] text-[var(--module-ink)]",
  green: "bg-sales-tint text-sales-ink",
  orange: "bg-crm-tint text-crm-ink",
  violet: "bg-marketing-tint text-marketing-ink",
  blue: "bg-projects-tint text-projects-ink",
  warn: "bg-warn-tint text-warn",
  red: "bg-danger-tint text-danger",
};
export function Badge({ tone = "neutral", children, className }: { tone?: Tone; children: ReactNode; className?: string }) {
  return <span className={cx("inline-flex items-center gap-1 rounded-[6px] px-2 py-0.5 text-xs font-medium whitespace-nowrap", TONES[tone], className)}>{children}</span>;
}

export function Dot({ color, size = 8 }: { color: string; size?: number }) {
  return <span aria-hidden="true" style={{ width: size, height: size, borderRadius: 2, background: color, flexShrink: 0, display: "inline-block" }} />;
}

// ── contenitori ───────────────────────────────────────────────────────
export function Panel({ title, icon, action, children, className, bodyClass }: { title?: ReactNode; icon?: IconName; action?: ReactNode; children: ReactNode; className?: string; bodyClass?: string }) {
  return (
    <section className={cx("kit-panel bg-surface border border-line rounded-card flex flex-col min-w-0", className)}>
      {title && (
        <div className="flex items-center gap-2.5 px-[18px] py-3.5 border-b border-line">
          {icon && <Icon name={icon} size={16} />}
          <h2 className="m-0 text-[15px] font-semibold">{title}</h2>
          <div className="ml-auto flex items-center gap-2">{action}</div>
        </div>
      )}
      <div className={bodyClass}>{children}</div>
    </section>
  );
}

/** Illustrazione degli stati vuoti: una scheda vuota con l'icona nel colore del modulo. */
export function EmptyArt({ icon, size = 1 }: { icon: IconName; size?: number }) {
  return (
    <div aria-hidden="true" className="relative" style={{ width: 120 * size, height: 84 * size }}>
      <svg width={120 * size} height={84 * size} viewBox="0 0 120 84" fill="none" className="absolute inset-0">
        <rect x="16" y="14" width="76" height="56" rx="10" transform="rotate(-8 54 42)" className="fill-[var(--module-tint)]" />
        <rect x="26.5" y="12.5" width="75" height="57" rx="10" className="fill-surface stroke-line" />
        <rect x="37" y="25" width="34" height="6" rx="3" className="fill-line" />
        <rect x="37" y="37" width="52" height="5" rx="2.5" className="fill-line-soft" />
        <rect x="37" y="48" width="40" height="5" rx="2.5" className="fill-line-soft" />
        <circle cx="10" cy="22" r="2.5" className="fill-[var(--module)] opacity-30" />
        <circle cx="112" cy="70" r="3" className="fill-[var(--module)] opacity-20" />
        <path d="M106 6l1.4 3.1L110.5 10.5l-3.1 1.4L106 15l-1.4-3.1L101.5 10.5l3.1-1.4z" className="fill-[var(--module)] opacity-50" />
      </svg>
      <motion.span
        initial={{ scale: 0.7, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 380, damping: 22, delay: 0.05 }}
        className="absolute rounded-xl bg-[var(--module)] text-white flex items-center justify-center shadow-[0_8px_16px_-8px_rgba(16,18,20,0.5)]"
        style={{ right: 4 * size, bottom: 0, width: 36 * size, height: 36 * size }}
      >
        <Icon name={icon} size={18 * size} />
      </motion.span>
    </div>
  );
}

export function Empty({ icon = "sparkles", title, text, action }: { icon?: IconName; title: string; text?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center text-center gap-2 py-12 px-6">
      <div className="mb-2">
        <EmptyArt icon={icon} />
      </div>
      <p className="m-0 font-semibold">{title}</p>
      {text && <p className="m-0 text-sm text-muted max-w-sm">{text}</p>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

/** Stato vuoto compatto dentro un Panel: illustrazione piccola, una frase utile e, se serve, un'azione. */
export function PanelEmpty({ icon, title, text, action }: { icon: IconName; title: string; text?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center gap-1.5 px-6 py-7">
      <div className="mb-1.5">
        <EmptyArt icon={icon} size={0.7} />
      </div>
      <p className="m-0 text-sm font-semibold">{title}</p>
      {text && <p className="m-0 text-[13px] text-muted max-w-[360px] leading-snug">{text}</p>}
      {action && <div className="mt-2.5">{action}</div>}
    </div>
  );
}

/** Stato vuoto di una pagina intera: niente riquadro, illustrazione grande, spiegazione e azioni. */
export function PageEmpty({ icon, title, text, actions, children }: { icon: IconName; title: string; text?: ReactNode; actions?: ReactNode; children?: ReactNode }) {
  return (
    <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, ease: "easeOut" }} className="flex flex-col items-center text-center gap-2 px-6 pt-[8vh] pb-10">
      <EmptyArt icon={icon} size={1.4} />
      <h2 className="m-0 mt-5 text-[22px] font-semibold tracking-[-0.02em]">{title}</h2>
      {text && <p className="m-0 text-sm text-muted max-w-[460px] leading-[1.55]">{text}</p>}
      {actions && <div className="mt-4 flex items-center gap-2 flex-wrap justify-center">{actions}</div>}
      {children && <div className="mt-8 w-full max-w-[560px]">{children}</div>}
    </motion.section>
  );
}

export function Loading({ label = "Caricamento" }: { label?: string }) {
  return (
    <div role="status" className="flex items-center justify-center gap-2 py-16 text-muted text-sm">
      <Spinner /> {label}
    </div>
  );
}

export function ErrorBox({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <div role="alert" className="rounded-[10px] border border-danger-line bg-danger-tint text-danger px-3 py-2 text-sm">
      {(error as Error).message ?? String(error)}
    </div>
  );
}

// ── overlay: modale e pannello laterale ─────────────────────────────────
// Esc chiude solo il livello aperto per ultimo (il menu prima del pannello che lo contiene)
const escStack: (() => void)[] = [];
window.addEventListener("keydown", (e) => e.key === "Escape" && escStack.at(-1)?.());

function useEsc(open: boolean, onClose: () => void) {
  const latest = useRef(onClose);
  latest.current = onClose;
  useEffect(() => {
    if (!open) return;
    const h = () => latest.current();
    escStack.push(h);
    return () => {
      const i = escStack.lastIndexOf(h);
      if (i >= 0) escStack.splice(i, 1);
    };
  }, [open]);
}

export function Modal({ open, onClose, title, children, footer, width = 520 }: { open: boolean; onClose: () => void; title: string; children: ReactNode; footer?: ReactNode; width?: number }) {
  useEsc(open, onClose);
  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[10vh] bg-scrim/30" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
          <motion.div role="dialog" aria-modal="true" aria-label={title} className="w-full bg-surface rounded-2xl border border-line shadow-[0_24px_60px_-20px_rgba(16,18,20,0.35)] flex flex-col max-h-[80vh]" style={{ maxWidth: width }} initial={{ y: 12, scale: 0.98 }} animate={{ y: 0, scale: 1 }} exit={{ y: 8, opacity: 0 }} transition={{ type: "spring", stiffness: 420, damping: 34 }}>
            <div className="flex items-center gap-3 px-5 pt-4 pb-3">
              <h2 className="m-0 text-lg font-semibold tracking-tight">{title}</h2>
              <IconButton icon="x" label="Chiudi" className="ml-auto" onClick={onClose} />
            </div>
            <div className="px-5 pb-4 overflow-y-auto flex flex-col gap-4">{children}</div>
            {footer && <div className="flex justify-end gap-2 px-5 py-3.5 border-t border-line">{footer}</div>}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** Spazio a destra del contenuto dove si aggancia il Drawer in modalità `docked` (vedi AppShell). */
export const SIDE_PANEL_SLOT = "kipo-side-panel";
const DOCK_MIN = 380;
let dockWidth: number | null = null; // larghezza scelta trascinando, ricordata per la sessione

function useWide() {
  const q = "(min-width: 1024px)";
  const [wide, setWide] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const on = () => setWide(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return wide;
}

/**
 * Pannello a destra. Di default sta sopra la pagina; con `docked` (schermi larghi) si aggancia
 * accanto al contenuto senza velo, la pagina resta usabile e si stringe mentre il pannello entra.
 * Il contenuto ha già il suo margine interno; con `bleed` va a filo dei bordi (pannelli a sezioni che gestiscono da sé il padding).
 */
export function Drawer({ open, onClose, title, subtitle, children, footer, width = 560, headerColor, docked, bleed = false }: { open: boolean; onClose: () => void; title: ReactNode; subtitle?: ReactNode; children: ReactNode; footer?: ReactNode; width?: number; headerColor?: string; docked?: boolean; bleed?: boolean }) {
  useEsc(open, onClose);
  const wide = useWide();
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setSlot(document.getElementById(SIDE_PANEL_SLOT));
  }, []);
  const [w, setW] = useState(() => dockWidth ?? width);
  // una sola barra laterale alla volta (come nel vecchio Planner): quella aperta per ultima chiude le altre
  const dockId = useId();
  const docking = !!docked && wide && open;
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!docking) return;
    window.dispatchEvent(new CustomEvent("kipo:dock", { detail: dockId }));
    const other = (e: Event) => (e as CustomEvent<string>).detail !== dockId && closeRef.current();
    window.addEventListener("kipo:dock", other);
    return () => window.removeEventListener("kipo:dock", other);
  }, [docking, dockId]);
  const [resizing, setResizing] = useState(false);
  const start = useRef<{ x: number; w: number } | null>(null);

  const header = (
    <div className="flex items-start gap-3 px-5 py-4 border-b border-line shrink-0" style={headerColor ? { background: headerColor, color: "#fff", borderColor: "transparent" } : undefined}>
      <div className="flex flex-col gap-1 min-w-0">
        <h2 className="m-0 text-lg font-semibold tracking-tight leading-snug">{title}</h2>
        {subtitle && <div className={cx("text-sm", headerColor ? "text-white/80" : "text-muted")}>{subtitle}</div>}
      </div>
      <button type="button" aria-label="Chiudi" onClick={onClose} className={cx("ml-auto h-8 w-8 rounded-lg flex items-center justify-center cursor-pointer shrink-0", headerColor ? "hover:bg-surface/15" : "hover:bg-hover")}>
        <Icon name="x" size={16} />
      </button>
    </div>
  );
  const body = (
    <>
      {header}
      <div className={cx("flex-1 overflow-y-auto", !bleed && "px-5 py-5")}>{children}</div>
      {footer && <div className="flex justify-end gap-2 px-5 py-3.5 border-t border-line shrink-0">{footer}</div>}
    </>
  );

  if (docked && wide && slot) {
    const resize = (x: number) => start.current && setW(Math.min(Math.floor(window.innerWidth / 2), Math.max(DOCK_MIN, start.current.w + start.current.x - x)));
    return createPortal(
      <AnimatePresence>
        {open && (
          // stessa animazione del vecchio Planner: 200 ms lineari, la pagina si stringe mentre il pannello entra da destra
          <motion.div key="dock" className="h-full overflow-hidden" initial={{ width: 0 }} animate={{ width: w }} exit={{ width: 0 }} transition={{ width: resizing ? { duration: 0 } : { duration: 0.2, ease: "linear" } }}>
            <aside role="complementary" aria-label={typeof title === "string" ? title : "Dettagli"} className="relative h-full bg-surface border-l border-line flex flex-col" style={{ width: w }}>
              <div
                role="separator"
                aria-orientation="vertical"
                aria-label="Ridimensiona il pannello"
                title="Trascina per ridimensionare, doppio clic per tornare alla larghezza iniziale"
                onPointerDown={(e) => ((start.current = { x: e.clientX, w }), setResizing(true), e.currentTarget.setPointerCapture(e.pointerId), (document.body.style.userSelect = "none"))}
                onPointerMove={(e) => resize(e.clientX)}
                onPointerUp={() => ((start.current = null), setResizing(false), (dockWidth = w), (document.body.style.userSelect = ""))}
                onPointerCancel={() => ((start.current = null), setResizing(false), (document.body.style.userSelect = ""))}
                onDoubleClick={() => ((dockWidth = null), setW(width))}
                className="group/resize absolute inset-y-0 -left-1 z-10 w-2 cursor-col-resize touch-none"
              >
                <span className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 transition-colors group-hover/resize:bg-[var(--module)] group-active/resize:bg-[var(--module)]" />
              </div>
              {body}
            </aside>
          </motion.div>
        )}
      </AnimatePresence>,
      slot,
    );
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div className="fixed inset-0 z-40 bg-scrim/20" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
          <motion.aside role="dialog" aria-modal="true" className="fixed right-0 top-0 bottom-0 z-50 bg-surface border-l border-line flex flex-col w-full shadow-[-24px_0_60px_-30px_rgba(16,18,20,0.4)]" style={{ maxWidth: width }} initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 40, opacity: 0 }} transition={{ type: "spring", stiffness: 380, damping: 36 }}>
            {body}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

// ── menu a tendina ────────────────────────────────────────────────────
/** `leading`: icona già colorata al posto di `icon`; `checked`: voce attuale, con la spunta. */
export type MenuItem = { label: string; icon?: IconName; leading?: ReactNode; checked?: boolean; onClick: () => void; danger?: boolean; disabled?: boolean };

const menuPanel = "min-w-[200px] max-h-80 overflow-y-auto rounded-xl border border-line bg-surface p-1 shadow-[0_12px_40px_-12px_rgba(16,18,20,0.3)]";

function MenuItems({ items, close }: { items: MenuItem[]; close: () => void }) {
  return (
    <>
      {items.map((it) => (
        <button key={it.label} role="menuitem" type="button" disabled={it.disabled} onClick={() => { close(); it.onClick(); }} className={cx("w-full flex items-center gap-2.5 h-9 px-2.5 rounded-lg text-sm text-left cursor-pointer hover:bg-hover focus-visible:bg-hover outline-none disabled:opacity-50", it.danger ? "text-danger" : "text-ink")}>
          {it.leading ?? (it.icon && <Icon name={it.icon} size={15} />)}
          <span className="flex-1">{it.label}</span>
          {it.checked && <Icon name="check" size={14} strokeWidth={2.2} />}
        </button>
      ))}
    </>
  );
}

/** Menu al tasto destro: stesse voci del menu "⋯", aperto dove si clicca. */
export function ContextMenu({ items, children, className }: { items: MenuItem[]; children: ReactNode; className?: string }) {
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!at) return;
    const close = (e: Event) => !ref.current?.contains(e.target as Node) && setAt(null);
    document.addEventListener("mousedown", close);
    document.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    ref.current?.querySelector<HTMLButtonElement>("[role=menuitem]")?.focus();
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [at]);
  useEsc(!!at, () => setAt(null));
  if (items.length === 0) return <div className={className}>{children}</div>;
  return (
    <div
      className={className}
      onContextMenu={(e) => {
        e.preventDefault();
        // resta dentro la finestra anche vicino ai bordi
        setAt({ x: Math.min(e.clientX, window.innerWidth - 216), y: Math.min(e.clientY, window.innerHeight - (items.length * 36 + 16)) });
      }}
    >
      {children}
      {at &&
        createPortal(
          <div ref={ref} role="menu" className={cx("fixed z-50", menuPanel)} style={{ left: at.x, top: at.y }}>
            <MenuItems items={items} close={() => setAt(null)} />
          </div>,
          document.body,
        )}
    </div>
  );
}

/** Menu a tendina: si apre in un livello sopra la pagina, così non viene tagliato da tabelle o pannelli che scorrono. */
export function Menu({ trigger, items, align = "right" }: { trigger: (p: { onClick: () => void; "aria-expanded": boolean }) => ReactNode; items: MenuItem[]; align?: "left" | "right" }) {
  const [at, setAt] = useState<React.CSSProperties | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const close = () => setAt(null);
  useEffect(() => {
    if (!at) return;
    const h = (e: Event) => !ref.current?.contains(e.target as Node) && !panel.current?.contains(e.target as Node) && setAt(null);
    document.addEventListener("mousedown", h);
    document.addEventListener("scroll", h, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", h);
      document.removeEventListener("scroll", h, true);
      window.removeEventListener("resize", close);
    };
  }, [at]);
  useEsc(!!at, close);
  const toggle = () => {
    if (at) return close();
    const r = ref.current!.getBoundingClientRect();
    const h = Math.min(items.length * 36 + 10, 320);
    // vicino al fondo della finestra si apre verso l'alto
    const top = r.bottom + 4 + h > window.innerHeight ? Math.max(8, r.top - 4 - h) : r.bottom + 4;
    setAt(align === "right" ? { top, right: window.innerWidth - r.right } : { top, left: r.left });
  };
  return (
    <div ref={ref} className="relative">
      {trigger({ onClick: toggle, "aria-expanded": !!at })}
      {at &&
        createPortal(
          <div ref={panel} role="menu" className={cx("fixed z-50", menuPanel)} style={at}>
            <MenuItems items={items} close={close} />
          </div>,
          document.body,
        )}
    </div>
  );
}

// ── toast ─────────────────────────────────────────────────────────────
type Toast = { id: number; text: string; tone: "ok" | "error" };
const ToastCtx = createContext<(text: string, tone?: "ok" | "error") => void>(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const push = useCallback((text: string, tone: "ok" | "error" = "ok") => {
    const id = Date.now() + Math.random();
    setItems((x) => [...x, { id, text, tone }]);
    setTimeout(() => setItems((x) => x.filter((t) => t.id !== id)), 3800);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div aria-live="polite" className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[60] flex flex-col gap-2 items-center pointer-events-none">
        <AnimatePresence>
          {items.map((t) => (
            <motion.div key={t.id} initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ opacity: 0 }} className={cx("pointer-events-auto rounded-xl px-4 py-2.5 text-sm font-medium shadow-lg flex items-center gap-2", t.tone === "ok" ? "bg-ink text-on-ink" : "bg-[#B42318] text-white")}>
              <Icon name={t.tone === "ok" ? "check" : "alert"} size={15} strokeWidth={2.2} />
              {t.text}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastCtx.Provider>
  );
}

// ── header strutturato di modulo (proposta C) ───────────────────────────
export interface Crumb {
  label: string;
  to?: string;
}

/**
 * Barra superiore nel colore del modulo + striscia di dati strutturati +
 * viste del modulo. È il segno distintivo della proposta C.
 */
export function ModuleHeader({ crumbs, title, badge, actions, after, stats, tabs }: { crumbs: Crumb[]; title: ReactNode; badge?: ReactNode; actions?: ReactNode; after?: ReactNode; stats?: { label: string; value: ReactNode; sub?: ReactNode; tone?: "warn" | "good"; progress?: number }[]; tabs?: { key: string; label: string; icon: IconName; to: string; params?: Record<string, string>; search?: Record<string, unknown>; count?: number; countTone?: "warn"; active: boolean }[] }) {
  const moduleIcon = MODULE_ICON[useRouterState({ select: (s) => s.location.pathname.split("/")[1] ?? "" })];
  return (
    <header className="bg-surface border-b border-line flex flex-col">
      {/* stessa struttura su ogni pagina del modulo: percorso a sinistra, azioni e ricerca sempre a destra */}
      <div className="@container min-h-16 flex items-center gap-2.5 px-4 sm:px-7 py-3 flex-wrap text-white [view-transition-name:page-header]" style={{ background: "var(--module)" }}>
        <nav aria-label="Percorso" className="flex items-center gap-1 min-w-0">
          {moduleIcon && (
            <span aria-hidden="true" className="w-7 h-7 mr-1 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
              <Icon name={moduleIcon} size={15} color="#fff" />
            </span>
          )}
          {crumbs.map((c) => (
            <span key={c.label} className="flex items-center gap-1 min-w-0">
              {c.to ? (
                <Link to={c.to} className="h-7 px-2 rounded-md flex items-center text-sm text-white/75 hover:text-white hover:bg-white/15 truncate max-w-[220px]">
                  {c.label}
                </Link>
              ) : (
                <span className="px-2 text-sm text-white/75 truncate max-w-[220px]">{c.label}</span>
              )}
              <Icon name="chevron-right" size={14} color="rgba(255,255,255,0.55)" strokeWidth={2} className="shrink-0" />
            </span>
          ))}
          <h1 aria-current="page" className={cx("m-0 text-[17px] font-semibold tracking-[-0.01em] truncate", crumbs.length > 0 && "px-2")}>{title}</h1>
        </nav>
        {badge}
        {after}
        <div className="ml-auto flex items-center gap-2 flex-wrap justify-end">
          {actions}
          <SearchTrigger onModule />
        </div>
      </div>
      {stats && stats.length > 0 && (
        <div className="scroll-x border-t border-line">
        <div className="grid min-w-[640px] md:min-w-0" style={{ gridTemplateColumns: `repeat(${stats.length}, minmax(0, 1fr))` }}>
          {stats.map((s, i) => (
            <div key={s.label} className={cx("py-3 flex flex-col gap-0.5 min-w-0 border-line", i === 0 ? "pl-4 sm:pl-7 pr-4" : "px-3 sm:px-5", i < stats.length - 1 && "border-r")}>
              <span className="text-xs text-muted truncate">{s.label}</span>
              <span className={cx("text-[15px] font-semibold truncate tabular", s.tone === "warn" && "text-warn", s.tone === "good" && "text-sales-ink")}>{s.value}</span>
              {s.progress !== undefined ? (
                <div className="h-1 rounded-sm bg-line-soft mt-1.5">
                  <div className="h-1 rounded-sm" style={{ width: `${Math.min(100, Math.max(0, s.progress))}%`, background: s.progress > 100 ? "var(--color-warn)" : "var(--module)" }} />
                </div>
              ) : (
                s.sub && <span className="text-xs text-muted truncate">{s.sub}</span>
              )}
            </div>
          ))}
        </div>
        </div>
      )}
      {tabs && (
        <nav aria-label="Viste" className="flex gap-0.5 px-2 sm:px-5 border-t border-line overflow-x-auto">
          {tabs.map((t) => (
            <Link key={t.key} to={t.to} params={t.params as never} search={t.search as never} aria-current={t.active ? "page" : undefined} className={cx("flex items-center gap-[7px] h-[42px] px-3 text-sm whitespace-nowrap border-b-2", t.active ? "font-medium text-ink border-[var(--module)]" : "text-muted border-transparent hover:text-ink")}>
              <Icon name={t.icon} size={15} />
              {t.label}
              {t.count !== undefined && t.count > 0 && <span className={cx("text-[11px] rounded-[5px] px-1.5 py-px", t.countTone === "warn" ? "bg-crm-tint text-warn font-semibold" : "bg-line-soft")}>{t.count}</span>}
            </Link>
          ))}
        </nav>
      )}
    </header>
  );
}

/** Header neutro (Panoramica, Impostazioni, Kipo AI). */
export function PageHeader({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <header className="@container min-h-16 flex items-center gap-3 px-4 sm:px-7 py-3 bg-surface border-b border-line flex-wrap [view-transition-name:page-header]">
      <h1 className="m-0 text-[17px] font-semibold tracking-[-0.01em]">{title}</h1>
      <div className="ml-auto flex items-center gap-2 flex-wrap justify-end">
        {children}
        <SearchTrigger />
      </div>
    </header>
  );
}

/**
 * Ricerca globale degli header: sempre in fondo a destra con larghezza fissa,
 * così non si sposta tra una pagina e l'altra; sparisce quando l'header è stretto.
 * Apre la palette ⌘K montata dall'AppShell.
 */
function SearchTrigger({ onModule }: { onModule?: boolean }) {
  return (
    <div className="hidden @min-[880px]:flex shrink-0 w-[240px] @min-[1200px]:w-[320px]">
      <button
        type="button"
        onClick={() => window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }))}
        className={cx(
          "flex w-full h-[38px] border rounded-[9px] items-center gap-2 px-3 min-w-0 text-sm cursor-pointer",
          onModule ? "border-white/30 bg-surface/15 text-white/80 hover:bg-white/20" : "border-line bg-ground text-subtle hover:border-line-strong",
        )}
      >
        <Icon name="search" size={16} color={onModule ? "rgba(255,255,255,0.8)" : "var(--color-subtle)"} />
        <span className="flex-1 text-left truncate">Cerca in tutta la suite</span>
        <span className={cx("text-[11px] border rounded-[5px] px-1.5", onModule ? "border-white/30 bg-surface/10" : "border-line bg-surface")}>⌘K</span>
      </button>
    </div>
  );
}

export function Toolbar({ children }: { children: ReactNode }) {
  return <div className="flex items-center gap-2 flex-wrap">{children}</div>;
}

/** Pulsante-filtro "a pillola" della toolbar. */
export function FilterButton({ active, children, onClick, icon }: { active?: boolean; children: ReactNode; onClick?: () => void; icon?: IconName }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={active} className={cx("h-[34px] px-3 rounded-lg border text-[13px] cursor-pointer flex items-center gap-2 whitespace-nowrap", active ? "border-ink bg-ink text-on-ink" : "border-line bg-surface hover:bg-ground")}>
      {icon && <Icon name={icon} size={14} />}
      {children}
    </button>
  );
}

export function Segmented<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: { value: T; label: string }[] }) {
  return (
    <div role="radiogroup" className="inline-flex self-start max-w-full overflow-x-auto rounded-lg border border-line bg-surface p-0.5">
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={value === o.value} onClick={() => onChange(o.value)} className={cx("h-[30px] px-3 rounded-md text-[13px] cursor-pointer", value === o.value ? "bg-ink text-on-ink" : "text-text2 hover:bg-hover")}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Tabella semplice con intestazioni sobrie; scorre in orizzontale su mobile. */
export function Table({ head, children, minWidth = 720 }: { head: (string | { label: string; align?: "right" | "left"; width?: number })[]; children: ReactNode; minWidth?: number }) {
  return (
    // dentro un Panel la cornice c'è già: niente bordo doppio
    <div className="bg-surface border border-line rounded-card scroll-x in-[.kit-panel]:border-0 in-[.kit-panel]:rounded-none">
      <table className="w-full border-collapse text-sm" style={{ minWidth }}>
        <thead>
          <tr>
            {head.map((h, i) => {
              const c = typeof h === "string" ? { label: h } : h;
              return (
                <th key={i} scope="col" style={{ width: c.width }} className={cx("text-xs font-medium text-muted px-4 py-2.5 border-b border-line whitespace-nowrap", c.align === "right" ? "text-right" : "text-left")}>
                  {c.label}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="[&>tr]:border-b [&>tr]:border-line-soft [&>tr:last-child]:border-0 [&_td]:px-4 [&_td]:py-2.5">{children}</tbody>
      </table>
    </div>
  );
}

export function Stat({ label, value, sub, tone }: { label: string; value: ReactNode; sub?: ReactNode; tone?: "warn" | "good" }) {
  return (
    <div className="bg-surface border border-line rounded-card px-4 py-3 flex flex-col gap-0.5 min-w-0">
      <span className="text-xs text-muted">{label}</span>
      <span className={cx("text-xl font-semibold tracking-tight tabular truncate", tone === "warn" && "text-warn", tone === "good" && "text-sales-ink")}>{value}</span>
      {sub && <span className="text-xs text-muted truncate">{sub}</span>}
    </div>
  );
}
